import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { Box, Button, PageLarge, SubPage, Tab, TabContextProvider, TabGroup } from '@/common/components';
import { GENETIC_COUNSELING_TRANSLATION } from '../../constants';
import { PATHS } from '@/common/constants';
import { Permissions } from '@/modules/permissions/types';
import { Sizes } from '@/common/types';
import { TabId } from './GeneticCounselingScreen.types';
import { useTab } from '@/common/components/hooks';

import RequirePermission from '@/modules/permissions/components/RequirePermission/RequirePermission';

export const GeneticCounselingScreen: FC = () => {
  const { t } = useTranslation(GENETIC_COUNSELING_TRANSLATION);
  const { selectedTab, setSelectedTab } = useTab();

  const location = useLocation();
  const navigate = useNavigate();

  const tabsList = [
    { label: t('referrals.tab'), value: TabId.REFERRALS, url: `${PATHS.GENETIC_COUNSELING.REFERRALS}` },
    { label: t('upcoming.tab'), value: TabId.UPCOMING, url: `${PATHS.GENETIC_COUNSELING.UPCOMING}` },
    { label: t('completed.tab'), value: TabId.COMPLETED, url: `${PATHS.GENETIC_COUNSELING.COMPLETED}` },
    { label: t('declined.tab'), value: TabId.DECLINED, url: `${PATHS.GENETIC_COUNSELING.DECLINED}` },
  ];

  const handleCreateNewReferral = (): void => {
    navigate(`/${PATHS.GENETIC_COUNSELING.ROOT}/${PATHS.GENETIC_COUNSELING.REFERRALS}/${PATHS.GENETIC_COUNSELING.NEW}`);
  };

  let actions;
  if (selectedTab === TabId.REFERRALS) {
    actions = (
      <RequirePermission to={Permissions.GENETIC_COUNSELING__CREATE}>
        <Button id="create-new-referral-btn" onClick={handleCreateNewReferral}>
          {t('create.access_button')}
        </Button>
      </RequirePermission>
    );
  }

  useEffect(() => {
    tabsList.forEach((tab) => {
      if (location.pathname.includes(tab.value)) {
        return setSelectedTab(tab.value);
      }
    });
  }, [location]);

  return (
    <>
      <TabContextProvider value={selectedTab}>
        <PageLarge
          header={
            <Box
              css={{
                boxShadow: '$tabGroup',
                marginTop: '$4',
              }}
            >
              <TabGroup
                css={{
                  borderBottom: 'none',
                  margin: '$1 auto',
                  maxWidth: '$studyInsights$containerMaxWidth',
                  padding: '0 $6',
                }}
                value={selectedTab}
                tabSize={Sizes.LARGE}
              >
                {tabsList.map((tab) => (
                  <Tab key={tab.value} label={tab.label} value={tab.value} url={tab.url} />
                ))}
              </TabGroup>
            </Box>
          }
          title={t('page_title')}
          actions={actions}
        >
          <SubPage>
            <Outlet />
          </SubPage>
        </PageLarge>
      </TabContextProvider>
    </>
  );
};

export default GeneticCounselingScreen;
